class MaedaaStickyCart extends HTMLElement {
  connectedCallback() {
    this.nativeButton = document.getElementById(this.dataset.form)?.querySelector('[type="submit"]');
    if (!this.nativeButton) return;
    this.button = this.querySelector('button');
    this.button.addEventListener('click', () => {
      if (!this.nativeButton.disabled) this.nativeButton.click();
    });
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.closest('product-info'), { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class'] });
    this.intersection = new IntersectionObserver(entries => {
      this.hidden = entries[0].isIntersecting;
    });
    this.intersection.observe(this.nativeButton);
    this.sync();
  }
  sync() {
    const disabled = this.nativeButton.disabled || this.nativeButton.classList.contains('loading');
    if (this.button.disabled !== disabled) this.button.disabled = disabled;
    const label = this.nativeButton.querySelector('span')?.textContent.trim();
    const output = this.querySelector('[data-label]');
    if (label && output.textContent !== label) output.textContent = label;
    const price = document.getElementById(`price-${this.dataset.section}`);
    const currentPrice = price?.querySelector(price.querySelector('.price--on-sale') ? '.price-item--sale' : '.price-item--regular')?.textContent.trim();
    const target = this.querySelector('[data-price]');
    if (currentPrice && target.textContent !== currentPrice) target.textContent = currentPrice;
  }
  disconnectedCallback() { this.observer?.disconnect(); this.intersection?.disconnect(); }
}
customElements.define('maedaa-sticky-cart', MaedaaStickyCart);

class MaedaaConfigurator extends HTMLElement {
  connectedCallback() {
    this.step = 0;
    this.items = {};
    this.pending = new Set();
    this.requests = {};
    this.count = 4;
    this.saving = false;
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'ar-EG', { style: 'currency', currency: this.dataset.currency });
    this.addEventListener('change', event => {
      const target = event.target;
      if (target.matches('[data-product-role]')) this.load(target.dataset.productRole, target.value);
      if (target.matches('[data-config-option]')) {
        const role = target.closest('[data-options]').dataset.options;
        const values = [...target.closest('[data-options]').querySelectorAll('select')].map(select => select.value);
        this.load(role, this.items[role]?.url || this.querySelector(`[data-product-role="${role}"]:checked`).value, values);
      }
      if (target.matches('[data-chair-count]')) { this.count = Number(target.value); this.render(); }
    });
    this.addEventListener('click', event => {
      if (event.target.closest('[data-next]')) this.go(this.step + 1);
      if (event.target.closest('[data-back]')) this.go(this.step - 1);
      const goto = event.target.closest('[data-goto]');
      if (goto && !goto.disabled) this.go(Number(goto.dataset.goto));
      if (event.target.closest('[data-add]')) this.add();
    });
    this.render();
  }
  async load(role, path, values) {
    this.requests[role]?.abort();
    const controller = new AbortController();
    this.requests[role] = controller;
    this.pending.add(role);
    delete this.items[role];
    this.querySelector('[data-status]').textContent = this.dataset.loading;
    this.render();
    try {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin) throw Error('Invalid product origin');
      url.searchParams.set('section_id', 'maedaa-config-product');
      if (values) url.searchParams.set('option_values', values.join(','));
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw Error('Product unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (this.requests[role] !== controller) return;
      const source = html.querySelector('[data-config-product]');
      if (!source) throw Error('Missing product');
      this.querySelector(`[data-options="${role}"]`).replaceChildren(source);
      const json = source.querySelector('[data-config-variant]');
      if (json) this.items[role] = JSON.parse(json.textContent);
      this.querySelector('[data-status]').textContent = this.items[role]?.available ? '' : this.dataset.unavailable;
    } catch (error) {
      if (error.name !== 'AbortError') this.querySelector('[data-status]').textContent = this.dataset.error;
    } finally {
      if (this.requests[role] === controller) { this.pending.delete(role); this.render(); }
    }
  }
  validQuantity(item, quantity) {
    const rule = item?.quantity_rule || {};
    return quantity >= (rule.min || 1) && (!rule.max || quantity <= rule.max) && quantity % (rule.increment || 1) === 0;
  }
  ready() {
    return !this.pending.size && this.items.table?.available && this.items.chair?.available && this.validQuantity(this.items.table, 1) && this.validQuantity(this.items.chair, this.count);
  }
  go(step) {
    if (step < 0 || step > 4 || this.pending.size || this.saving) return;
    if (step > 0 && !this.items.table) return;
    if (step > 1 && !this.items.chair) return;
    this.step = step;
    this.querySelectorAll('[data-step]').forEach(panel => panel.hidden = Number(panel.dataset.step) !== step);
    this.render();
    this.querySelector(`[data-step="${step}"] h2`).focus();
  }
  render() {
    const busy = this.pending.size > 0 || this.saving;
    this.querySelector('[data-back]').disabled = this.step === 0 || busy;
    this.querySelector('[data-next]').hidden = this.step === 4;
    this.querySelector('[data-next]').disabled = busy || (this.step === 0 && !this.items.table) || (this.step === 1 && !this.items.chair);
    this.querySelector('[data-add]').disabled = !this.ready() || busy || this.step !== 4;
    this.querySelectorAll('[data-goto]').forEach(button => {
      const n = Number(button.dataset.goto);
      button.disabled = busy || (n > 0 && !this.items.table) || (n > 1 && !this.items.chair);
      if (n === this.step) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
    });
    for (const role of ['table','chair']) {
      const item = this.items[role];
      this.querySelector(`[data-summary-${role}]`).textContent = item ? `${item.product} · ${item.title}` : '';
    }
    this.querySelector('[data-summary-count]').textContent = this.items.chair ? `× ${this.count}` : '';
    this.querySelector('[data-total]').textContent = this.ready() ? this.money.format((this.items.table.price + this.items.chair.price * this.count) / 100) : '—';
    this.querySelectorAll('input, select').forEach(control => control.disabled = this.saving);
    this.setAttribute('aria-busy', busy ? 'true' : 'false');
  }
  async add() {
    if (!this.ready() || this.saving || this.step !== 4) return;
    this.saving = true;
    this.render();
    this.querySelector('[data-status]').textContent = this.dataset.loading;
    const drawer = document.querySelector('cart-drawer');
    drawer?.setActiveElement(this.querySelector('[data-add]'));
    const group = crypto.randomUUID();
    const config = `${this.items.table.product}: ${this.items.table.title} / ${this.items.chair.product}: ${this.items.chair.title}`;
    const properties = { 'التكوين': config, '_maedaa_configuration': group };
    const body = { items: [{ id: this.items.table.id, quantity: 1, properties }, { id: this.items.chair.id, quantity: this.count, properties }], sections: drawer?.getSectionsToRender().map(section => section.id) || ['cart-icon-bubble'], sections_url: location.pathname };
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json();
      if (!response.ok || result.status) throw Error(result.description || this.dataset.error);
      this.querySelector('[data-status]').textContent = '';
      if (drawer && result.sections?.['cart-drawer']) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(result);
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'maedaa-configurator', cartData: result });
      } else location.assign(`${window.Shopify.routes.root}cart`);
    } catch (error) { this.querySelector('[data-status]').textContent = error.message || this.dataset.error; }
    finally { this.saving = false; this.render(); }
  }
  disconnectedCallback() { Object.values(this.requests).forEach(controller => controller.abort()); }
}
customElements.define('maedaa-configurator', MaedaaConfigurator);
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-scroll][data-target]');
  if (!button) return;
  const track = document.getElementById(button.dataset.target);
  if (!track) return;
  const card = track.firstElementChild;
  const amount = card ? card.getBoundingClientRect().width + 12 : track.clientWidth * .75;
  track.scrollBy({left: (button.dataset.scroll === 'next' ? 1 : -1) * amount * (document.documentElement.dir === 'rtl' ? -1 : 1),behavior:'smooth'});
});
